'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'

interface Workshop {
  id: string
  slug: string
  title: string
  description: string | null
  date: string
  duration: number
  meetingLink: string | null
  isActive: boolean
}

export default function WorkshopsPage() {
  const [workshops, setWorkshops] = useState<Workshop[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchWorkshops() {
      try {
        const res = await fetch('/api/workshops')
        if (res.ok) {
          const data = await res.json()
          setWorkshops(data)
        }
      } catch (error) {
        console.error('Failed to fetch workshops:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchWorkshops()
  }, [])

  // Format date for display
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const time = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York'
    })
    return `${time} ET`
  }

  // Parse date parts for the date card
  const getDateParts = (dateStr: string) => {
    const date = new Date(dateStr)
    return {
      month: date.toLocaleDateString('en-US', { month: 'short', timeZone: 'America/New_York' }),
      day: date.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'America/New_York' }),
      year: date.toLocaleDateString('en-US', { year: 'numeric', timeZone: 'America/New_York' }),
    }
  }

  return (
    <div className="min-h-screen bg-[#FDF8F0] font-sans -mt-20">
      {/* Hero */}
      <div className="relative pt-32 pb-20 px-6 text-center overflow-hidden">
        <div className="absolute -top-[200px] -right-[200px] w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(139,170,139,0.12)_0%,transparent_70%)] rounded-full" />
        <div className="absolute -bottom-[100px] -left-[150px] w-[400px] h-[400px] bg-[radial-gradient(circle,rgba(193,123,74,0.08)_0%,transparent_70%)] rounded-full" />

        <div className="max-w-[720px] mx-auto relative z-10">
          <div className="text-xs font-bold text-[#8BAA8B] uppercase tracking-widest mb-4">
            Between Sessions
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#2D2D2D] leading-[1.15] mb-6">
            Free Workshops for Therapists
          </h1>
          <p className="text-lg text-[#7A7A7A] max-w-[520px] mx-auto leading-relaxed">
            Practical, no-fluff workshops to help you navigate your career — from exam prep to building your practice.
          </p>
        </div>
      </div>

      {/* Workshops List */}
      <section className="px-6 pb-24">
        <div className="max-w-[900px] mx-auto">
          {loading ? (
            <div className="text-center py-16">
              <div className="w-12 h-12 border-4 border-[#D4E4D4] border-t-[#5E7D5E] rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[#7A7A7A]">Loading workshops...</p>
            </div>
          ) : workshops.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#7A7A7A]">No upcoming workshops at the moment.</p>
            </div>
          ) : (
            workshops.map((workshop) => {
              const dateParts = getDateParts(workshop.date)

              return (
                <Link
                  key={workshop.id}
                  href={`/workshops/${workshop.slug}`}
                  className="block bg-white rounded-2xl p-8 md:p-10 shadow-[0_2px_20px_rgba(0,0,0,0.06)] border border-[#8BAA8B]/10 hover:border-[#8BAA8B]/30 hover:shadow-[0_4px_30px_rgba(0,0,0,0.08)] transition-all group mb-6"
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
                    {/* Date Card */}
                    <div className="flex-shrink-0">
                      <div className="w-[100px] bg-[#5E7D5E] rounded-xl text-center py-4 text-white">
                        <div className="text-sm font-medium opacity-90">
                          {dateParts.month}
                        </div>
                        <div className="font-serif text-3xl font-bold">
                          {dateParts.day}
                        </div>
                        <div className="text-xs font-medium opacity-75 mt-1">
                          {formatTime(workshop.date)}
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="bg-gradient-to-r from-[#5E7D5E] to-[#4A6B4A] text-white px-3 py-1 rounded-full text-xs font-bold">
                          Free
                        </span>
                      </div>

                      <h2 className="font-serif text-2xl md:text-3xl text-[#2D2D2D] mb-2 group-hover:text-[#5E7D5E] transition-colors">
                        {workshop.title}
                      </h2>
                      <p className="text-[#7A7A7A] mb-4">
                        {workshop.description}
                      </p>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-[#7A7A7A] mb-5">
                        <span className="flex items-center gap-1.5">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          {formatTime(workshop.date)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="13" r="8" />
                            <path d="M12 9v4l2 2" />
                            <path d="M9 2h6" />
                            <path d="M12 2v2" />
                          </svg>
                          {workshop.duration} min
                        </span>
                        <span className="flex items-center gap-1.5">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 10l-4 4l6 6l4 -16l-16 4l6 6l4 -4" />
                          </svg>
                          Virtual (Google Meet)
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-[#C17B4A] font-semibold text-sm group-hover:gap-3 transition-all">
                        Learn more & register
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              )
            })
          )}

          {/* Empty state / Coming Soon */}
          <div className="mt-12 text-center">
            <div className="inline-block bg-white rounded-2xl px-8 py-6 border border-dashed border-[#8BAA8B]/30">
              <p className="text-[#7A7A7A] text-sm">
                More workshops coming soon. Have a topic you'd like us to cover?
              </p>
              <a
                href="mailto:betweensessions@alli.io"
                className="text-[#5E7D5E] font-medium text-sm hover:underline"
              >
                Email us at betweensessions@alli.io
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter / Stay Updated */}
      <section className="py-16 px-6 bg-[#EDF5ED]">
        <div className="max-w-[600px] mx-auto text-center">
          <h3 className="font-serif text-2xl text-[#2D2D2D] mb-3">
            Stay in the Loop
          </h3>
          <p className="text-[#7A7A7A] mb-6">
            Get notified when new workshops are announced. No spam, just opportunities to learn and grow.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-[400px] mx-auto">
            <input
              type="email"
              placeholder="your@email.com"
              className="flex-1 px-4 py-3 border-[1.5px] border-[#D4E4D4] rounded-lg bg-white text-[#2D2D2D] focus:border-[#8BAA8B] focus:ring-2 focus:ring-[#8BAA8B]/15 outline-none transition-all"
            />
            <button className="px-6 py-3 bg-[#5E7D5E] hover:bg-[#4A6B4A] text-white rounded-lg font-semibold transition-colors">
              Notify Me
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}
